// ---------------------------------------------------------------------------
// Cut & Crimp cable size calculator.
//
// This page answers "what size do I need", not "is this size OK". Every other
// UK tool demands the size as an input, which cannot answer the question people
// actually type. So this one solves for the size.
//
// The physics lives in cc-sizing.js, which is generated from
// hme-admin/lib/configurator/sizing.mjs and is byte-identical to it. That file
// is unit tested there and `npm run test` fails if the two drift, so the answer
// a customer is given is the answer the test suite proves correct. Do not
// reimplement any of it here.
//
// Nothing engineering is hardcoded in this file. Resistance, ampacity, derating
// factors, chemistry presets and the fuse ladder all come from the hub, so
// correcting a spec is a data edit in admin and never a theme deploy.
// ---------------------------------------------------------------------------

import { size } from "./cc-sizing.js";

const CACHE_KEY = "cc-sizing-payload";
const CACHE_TTL_MS = 10 * 60 * 1000;

// ---------------------------------------------------------------------------
// Payload. Same discipline as the configurator: a failure leaves the
// server-rendered fallback in place rather than showing a broken tool, and a
// cached copy whose version does not match is never computed from.
// ---------------------------------------------------------------------------
async function loadPayload(apiUrl) {
  let cached = null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) cached = JSON.parse(raw);
  } catch {
    // Private browsing, or a corrupted entry. Not worth reporting.
  }
  if (cached && Date.now() - cached.at < CACHE_TTL_MS && cached.payload?.version) {
    return cached.payload;
  }

  const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`sizing API ${res.status}`);
  const payload = await res.json();

  if (!payload?.version || !Array.isArray(payload.families) || payload.families.length === 0) {
    throw new Error("sizing API returned an unexpected shape");
  }

  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), payload }));
  } catch {
    // Storage full or blocked. The tool still works, it just refetches.
  }
  return payload;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
const esc = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

const fmtV = (n) => n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
const fmtPct = (n) => n.toFixed(2);
const fmtA = (n) => (Math.abs(n - Math.round(n)) < 0.05 ? String(Math.round(n)) : n.toFixed(1));

function el(html) {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

// ---------------------------------------------------------------------------
// State, and the URL it round trips through.
//
// Every input is in the query string so a result is shareable and linkable. An
// installer can send a colleague the exact case rather than describe it. Values
// that do not parse fall back to the default rather than throwing, because a
// mangled link should still show a working calculator.
// ---------------------------------------------------------------------------
const DEFAULTS = {
  v: 12,
  chem: "lead_acid",
  mode: "watts",
  load: 2000,
  len: 2,
  env: "cabin",
  inst: "free_air",
  n: 4,
  crit: "general",
  fam: "tinned",
};

function readState(payload) {
  const q = new URLSearchParams(location.search);
  const s = { ...DEFAULTS };

  const v = Number(q.get("v"));
  if ([12, 24, 48].includes(v)) s.v = v;

  const chem = q.get("chem");
  if (chem && payload.chemistry[chem]) s.chem = chem;

  const mode = q.get("mode");
  if (mode === "watts" || mode === "amps") s.mode = mode;

  const load = Number(q.get("load"));
  if (Number.isFinite(load) && load > 0) s.load = load;

  const len = Number(q.get("len"));
  if (Number.isFinite(len) && len > 0) s.len = len;

  const env = q.get("env");
  if (env && payload.derating.environments?.[env]) s.env = env;

  const inst = q.get("inst");
  if (inst === "free_air" || inst === "conduit" || inst === "bundled") s.inst = inst;

  const n = Number(q.get("n"));
  if (Number.isFinite(n) && n >= 2) s.n = Math.round(n);

  const crit = q.get("crit");
  if (crit && payload.criteria[crit] !== undefined) s.crit = crit;

  const fam = q.get("fam");
  if (fam && payload.families.some((f) => f.key === fam)) s.fam = fam;

  return s;
}

function writeState(s) {
  const q = new URLSearchParams({
    v: s.v,
    chem: s.chem,
    mode: s.mode,
    load: s.load,
    len: s.len,
    env: s.env,
    inst: s.inst,
    crit: s.crit,
    fam: s.fam,
  });
  if (s.inst === "bundled") q.set("n", s.n);
  history.replaceState(null, "", `${location.pathname}?${q}`);
}

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------
function seg(name, legend, options, current, hint) {
  const items = options
    .map(
      (o, i) => `
      <input type="radio" name="${name}" id="${name}-${i}" value="${esc(o.value)}"${
        String(o.value) === String(current) ? " checked" : ""
      }>
      <label for="${name}-${i}">${esc(o.label)}</label>`,
    )
    .join("");
  return `
    <fieldset class="cc-field">
      <span class="cc-legend">${esc(legend)}</span>
      <div class="cc-seg">${items}</div>
      ${hint ? `<p class="cc-hint">${esc(hint)}</p>` : ""}
    </fieldset>`;
}

function buildForm(payload, s) {
  const envs = payload.derating.environments || {};
  const envOptions = Object.entries(envs)
    .map(([k, e]) => `<option value="${esc(k)}"${k === s.env ? " selected" : ""}>${esc(e.label || k)}</option>`)
    .join("");

  const famOptions = payload.families
    .map((f) => `<option value="${esc(f.key)}"${f.key === s.fam ? " selected" : ""}>${esc(f.label)}</option>`)
    .join("");

  const chemOptions = Object.entries(payload.chemistry).map(([k, c]) => ({ value: k, label: c.label || k }));

  return `
    <div class="cc-panel cc-inputs-panel">
      <h2>Your circuit</h2>

      ${seg("cc-v", "System voltage", [
        { value: 12, label: "12V" },
        { value: 24, label: "24V" },
        { value: 48, label: "48V" },
      ], s.v)}

      ${seg("cc-chem", "Battery chemistry", chemOptions, s.chem,
        "Sets the battery voltage we assume for the functional check. It never changes the percentage, which is always of nominal.")}

      ${seg("cc-mode", "How do you know the load?", [
        { value: "watts", label: "Watts" },
        { value: "amps", label: "Amps" },
      ], s.mode)}

      <div class="cc-field">
        <label for="cc-load" id="cc-load-label">${s.mode === "watts" ? "Continuous watts" : "Continuous amps"}</label>
        <div class="cc-inline">
          <input type="number" id="cc-load" inputmode="decimal" min="1" step="any" value="${esc(s.load)}">
          <span class="cc-unit" data-cc-load-unit>${s.mode === "watts" ? "W" : "A"}</span>
        </div>
        <p class="cc-hint" data-cc-derived></p>
      </div>

      <div class="cc-field">
        <label for="cc-len">Run length, one way</label>
        <div class="cc-inline">
          <input type="number" id="cc-len" inputmode="decimal" min="0.1" step="0.1" value="${esc(s.len)}">
          <span class="cc-unit">metres</span>
        </div>
        <p class="cc-hint">Measure one way, battery to load. We double it for the return leg.</p>
      </div>

      <div class="cc-field">
        <label for="cc-fam">Cable</label>
        <select id="cc-fam">${famOptions}</select>
      </div>

      <div class="cc-field">
        <label for="cc-env">Where does it run?</label>
        <select id="cc-env">${envOptions}</select>
        <p class="cc-hint">Sets the conductor temperature. Nobody else asks this, and on a warm run it changes the answer.</p>
      </div>

      <div class="cc-field">
        <label for="cc-inst">How does it run?</label>
        <select id="cc-inst">
          <option value="free_air"${s.inst === "free_air" ? " selected" : ""}>On its own, in open air</option>
          <option value="bundled"${s.inst === "bundled" ? " selected" : ""}>Bundled with other cables</option>
          <option value="conduit"${s.inst === "conduit" ? " selected" : ""}>In conduit or trunking</option>
        </select>
        <div class="cc-inline" data-cc-bundle-wrap${s.inst === "bundled" ? "" : " hidden"} style="margin-top:.5rem">
          <input type="number" id="cc-n" inputmode="numeric" min="2" step="1" value="${esc(s.n)}" aria-label="How many cables in the bundle">
          <span class="cc-unit">cables in the bundle</span>
        </div>
      </div>

      ${seg("cc-crit", "What does it feed?", [
        { value: "critical", label: "Safety critical" },
        { value: "general", label: "General circuit" },
      ], s.crit,
        "Safety critical covers bilge pumps, blowers, navigation lights and electronics. General covers cabin lighting and everything where a bit of drop does not matter.")}
    </div>`;
}

// ---------------------------------------------------------------------------
// The answer
// ---------------------------------------------------------------------------
const GOVERNS = {
  volt_drop: "Your run is volt drop limited, not thermally limited. The length is what decided the size.",
  ampacity: "Your run is thermally limited, not volt drop limited. The current is what decided the size.",
  both: "This run is over on both checks.",
};

function checkCard(title, verdict, big, rows) {
  const dl = rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${v}</dd>`).join("");
  return `
    <div class="cc-check">
      <h3>${esc(title)}</h3>
      <p class="cc-big">${big}</p>
      <p class="cc-verdict cc-verdict--${verdict.ok ? "pass" : "fail"}"><span>${esc(verdict.text)}</span></p>
      <dl>${dl}</dl>
    </div>`;
}

function renderResult(payload, s, result) {
  const parts = [];
  const criteria = payload.criteria;

  if (!result.ok) {
    return `<div class="cc-answer cc-answer--none">
      <h2>We cannot size that</h2>
      <p class="cc-governs">${esc(result.problems.join(". "))}.</p>
    </div>`;
  }

  const cur = result.current;
  const family = payload.families.find((f) => f.key === s.fam);

  if (!result.recommendation) {
    parts.push(`
      <div class="cc-answer cc-answer--none">
        <h2>Nothing we stock will do this run</h2>
        <p class="cc-governs">${esc(result.reason)}</p>
      </div>
      <div class="cc-panel">
        <p><strong>Ring us on <a href="tel:02380455129">023 8045 5129</a></strong> and we will work it out with you.
        A 120mm&sup2; made-to-order run, parallel conductors or a shorter route will all get you there.</p>
      </div>`);
    return parts.join("");
  }

  const r = result.recommendation;
  const critPct = criteria.critical;
  const genPct = criteria.general;

  parts.push(`
    <div class="cc-answer">
      <h2 class="cc-sr">Recommended cable</h2>
      <p class="cc-size">${esc(r.csa)}mm&sup2; ${esc(family ? family.label.toLowerCase() : "")}</p>
      <p class="cc-governs">${esc(GOVERNS[r.governing] || "")}</p>
    </div>`);

  parts.push(`<div class="cc-checks">
    ${checkCard(
      "Volt drop",
      { ok: r.voltDropOk, text: r.voltDropOk ? `within your ${result.criterionPercent}% budget` : `over your ${result.criterionPercent}% budget` },
      `${fmtPct(r.pctOfNominal)}%`,
      [
        ["In volts", `${fmtV(r.voltDrop)}V`],
        [`Against ${critPct}% safety critical`, `${fmtV((critPct / 100) * s.v)}V budget`],
        [`Against ${genPct}% general`, `${fmtV((genPct / 100) * s.v)}V budget`],
        ["Voltage arriving at the load", r.voltageAtLoad === null ? "not available" : `${fmtV(r.voltageAtLoad)}V`],
      ],
    )}
    ${checkCard(
      "Current rating",
      {
        ok: r.ampacityOk,
        text: r.ampacityOk
          ? `carries the ${fmtA(r.protectionAmps)}A it has to`
          : `below the ${fmtA(r.protectionAmps)}A it has to carry`,
      },
      `${fmtA(r.ampacity.derated)}A`,
      [
        ["Your load", `${fmtA(cur.amps)}A`],
        [
          "Has to carry",
          r.protectionAmps > cur.amps
            ? `${fmtA(r.protectionAmps)}A, the fuse rating`
            : `${fmtA(r.protectionAmps)}A`,
        ],
        ["Manufacturer nominal", `${fmtA(r.ampacity.freeAir)}A`],
        ["Bundling factor", `x ${r.ampacity.kBundle}`],
        ["Location factor", `x ${r.ampacity.kTemp}`],
        ["Conductor temperature", `${result.conductorTempC}&deg;C`],
      ],
    )}
  </div>`);

  parts.push(`<p class="cc-hint" style="margin-top:.5rem">
    The percentage is of nominal system voltage (${esc(s.v)}.0V), which is what BS EN ISO 13297 measures against.
    The voltage at the load is a separate, functional check, taken from the minimum battery terminal voltage
    we assume for ${esc(payload.chemistry[s.chem]?.label || s.chem)}.
  </p>`);

  // Fuse
  const f = result.fuse;
  if (f && f.fuse) {
    parts.push(`
      <div class="cc-panel">
        <h2>Fuse</h2>
        <p><strong>${esc(f.fuse.label)}</strong>${
          f.fuse.holder ? `, in a ${esc(f.fuse.holder.label)}` : ""
        }.</p>
        <p class="cc-hint">Chosen as the smallest rating we stock at or above ${fmtA(f.minRating)}A,
        which is your ${fmtA(cur.amps)}A load plus continuous-duty headroom. The cable is then sized to carry
        that fuse, not just the load: a fuse above the cable's rating protects the fuse and not the cable.</p>
        ${
          !f.fuse.holder
            ? `<p class="cc-flag"><strong>We do not stock a holder for that fuse at ${esc(r.csa)}mm&sup2;.</strong>
               Our MEGA holders take up to 35mm&sup2;. Ring us and we will sort the termination.</p>`
            : ""
        }
        ${
          f.fuse.voltageRatingDc === null
            ? `<p class="cc-hint">We do not yet hold a written DC voltage rating for this fuse from our supplier,
               so we will not offer it on a 48V system. On 12V and 24V it is well inside any rating this class carries.</p>`
            : ""
        }
      </div>`);
  } else if (f) {
    parts.push(`
      <div class="cc-panel">
        <h2>Fuse</h2>
        <p class="cc-flag"><strong>We cannot recommend a fuse for this one.</strong> ${esc(f.reason)}</p>
        <p class="cc-hint">Ring us on <a href="tel:02380455129">023 8045 5129</a> and we will work out the protection with you.</p>
      </div>`);
  }

  // Neighbours
  const nb = [];
  if (result.sizeBelow) {
    nb.push(`<div class="cc-neighbour"><strong>${result.sizeBelow.csa}mm&sup2; below</strong>
      ${fmtPct(result.sizeBelow.pctOfNominal)}% drop, ${fmtA(result.sizeBelow.ampacity.derated)}A derated.
      ${result.sizeBelow.passes ? "Also passes." : "Does not pass."}</div>`);
  }
  if (result.sizeAbove) {
    nb.push(`<div class="cc-neighbour"><strong>${result.sizeAbove.csa}mm&sup2; above</strong>
      ${fmtPct(result.sizeAbove.pctOfNominal)}% drop, ${fmtA(result.sizeAbove.ampacity.derated)}A derated.
      Buys you margin.</div>`);
  }
  if (nb.length) parts.push(`<div class="cc-neighbours">${nb.join("")}</div>`);

  // Warnings
  for (const w of result.warnings || []) {
    parts.push(`<div class="cc-flag"><strong>Worth knowing.</strong> ${esc(w.message)}</div>`);
  }

  // Working
  parts.push(`
    <details class="cc-working">
      <summary>Show the working</summary>
      <div>
        <div class="cc-scroll">
        <table>
          <tbody>
            <tr><th>Current</th><td>${fmtA(cur.amps)}A${
              cur.derivedFrom === "watts" ? ` (${esc(s.load)}W at ${esc(s.v)}V, ${cur.efficiency * 100}% efficiency)` : ""
            }</td></tr>
            <tr><th>Resistance at 20&deg;C</th><td>${r.resistance.atTwentyC} m&#8486;/m</td></tr>
            <tr><th>Basis</th><td class="cc-prose">${esc(r.resistance.basis)}</td></tr>
            <tr><th>Source</th><td class="cc-prose">${esc(r.resistance.source)}</td></tr>
            <tr><th>Conductor temperature</th><td>${result.conductorTempC}&deg;C</td></tr>
            <tr><th>Temperature correction</th><td>(234.5 + ${result.conductorTempC}) / 254.5 = ${r.resistance.multiplier.toFixed(4)}</td></tr>
            <tr><th>Corrected resistance</th><td>${r.resistance.corrected.toFixed(4)} m&#8486;/m</td></tr>
            <tr><th>Volt drop</th><td>2 x ${esc(s.len)}m x ${(r.resistance.corrected / 1000).toFixed(6)} &#8486;/m x ${fmtA(cur.amps)}A = ${fmtV(r.voltDrop)}V</td></tr>
            <tr><th>As a percentage</th><td>100 x ${fmtV(r.voltDrop)} / ${esc(s.v)}.0 = ${fmtPct(r.pctOfNominal)}%</td></tr>
            <tr><th>Current rating basis</th><td class="cc-prose">${esc(r.ampacity.basis)}</td></tr>
            <tr><th>Derated rating</th><td>${fmtA(r.ampacity.freeAir)}A x ${r.ampacity.kBundle} x ${r.ampacity.kTemp} = ${fmtA(r.ampacity.derated)}A</td></tr>
          </tbody>
        </table>
        </div>
        <p class="cc-hint" style="margin-top:.75rem">
          The derating factors are ours, following ABYC E-11 practice. The manufacturer publishes a nominal
          current rating and states no conditions with it.
        </p>
      </div>
    </details>`);

  return parts.join("");
}

function standardsNote() {
  return `
    <div class="cc-standards">
      <h3>The standards this is worked to</h3>
      <ul>
        <li><strong>BS EN ISO 13297:2021</strong> requires that calculated volt drop does not exceed
        <strong>10% of nominal voltage</strong>. For equipment vital to safety it requires that the equipment be
        supplied with the proper voltage to achieve its rated performance, and offers 3% as an acceptable figure
        for such equipment. The 3% is a note against a functional requirement, not a limit the standard sets.</li>
        <li><strong>ABYC E-11</strong> is the US practice and makes both numbers mandatory, 3% and 10%. Its tables
        are based on a constant that corresponds to a conductor at about 29&deg;C, so they are a room temperature
        basis and do not allow for a warm engine bay.</li>
        <li><strong>BS EN 60228</strong> classifies the conductor. Our cable is Class 5 flexible, and the tinned
        range is declared to it by the manufacturer.</li>
        <li>Conductor resistance is corrected for temperature by (234.5 + T) / 254.5. A 60&deg;C conductor carries
        15.7% more resistance than the same cable at the 20&deg;C its datasheet figure is quoted at.</li>
      </ul>
      <p>This is a design aid and not a design. The installer remains responsible for the installation.</p>
    </div>`;
}

// ---------------------------------------------------------------------------
// Plain text, for copying and for the printed sheet
// ---------------------------------------------------------------------------
function asText(payload, s, result, dateStr) {
  const L = [];
  L.push("CUT & CRIMP CABLE SIZE CALCULATION");
  L.push(dateStr);
  L.push("");
  L.push("INPUTS");
  L.push(`  System            ${s.v}V ${payload.chemistry[s.chem]?.label || s.chem}`);
  L.push(`  Load              ${s.mode === "watts" ? `${s.load}W` : `${s.load}A`}`);
  L.push(`  Run length        ${s.len}m one way`);
  L.push(`  Cable             ${payload.families.find((f) => f.key === s.fam)?.label || s.fam}`);
  L.push(`  Location          ${payload.derating.environments?.[s.env]?.label || s.env}`);
  L.push(`  Installation      ${s.inst === "bundled" ? `bundled with ${s.n}` : s.inst.replace("_", " ")}`);
  L.push(`  Circuit type      ${s.crit === "critical" ? "safety critical" : "general"}`);
  L.push("");

  if (!result.ok) {
    L.push(`CANNOT SIZE: ${result.problems.join("; ")}`);
    return L.join("\n");
  }
  if (!result.recommendation) {
    L.push(`NO STOCKED SIZE PASSES: ${result.reason}`);
    return L.join("\n");
  }

  const r = result.recommendation;
  L.push("RECOMMENDATION");
  L.push(`  ${r.csa}mm2 ${payload.families.find((f) => f.key === s.fam)?.label || ""}`);
  L.push(`  ${GOVERNS[r.governing] || ""}`);
  L.push("");
  L.push("CHECKS");
  L.push(`  Current           ${fmtA(result.current.amps)}A`);
  L.push(`  Volt drop         ${fmtV(r.voltDrop)}V, ${fmtPct(r.pctOfNominal)}% of nominal`);
  L.push(`  Criterion         ${result.criterionPercent}%  (${r.voltDropOk ? "PASS" : "OVER"})`);
  L.push(`  Voltage at load   ${r.voltageAtLoad === null ? "n/a" : `${fmtV(r.voltageAtLoad)}V`}`);
  L.push(`  Derated rating    ${fmtA(r.ampacity.derated)}A  (${r.ampacityOk ? "PASS" : "OVER"})`);
  L.push(`  Conductor temp    ${result.conductorTempC}C`);
  L.push(`  Resistance used   ${r.resistance.atTwentyC} mohm/m at 20C, corrected x${r.resistance.multiplier.toFixed(4)}`);
  L.push(`  Resistance basis  ${r.resistance.basis}`);
  L.push("");
  if (result.fuse && result.fuse.fuse) {
    L.push("PROTECTION");
    L.push(`  ${result.fuse.fuse.label}${result.fuse.fuse.holder ? `, in a ${result.fuse.fuse.holder.label}` : ", no stocked holder at this size"}`);
    L.push("");
  } else if (result.fuse) {
    L.push("PROTECTION");
    L.push(`  No stocked fuse fits: ${result.fuse.reason}`);
    L.push("");
  }
  for (const w of result.warnings || []) L.push(`NOTE: ${w.message}`);
  if ((result.warnings || []).length) L.push("");
  L.push("STANDARDS");
  L.push("  BS EN ISO 13297:2021 requires calculated volt drop not to exceed 10% of nominal.");
  L.push("  For equipment vital to safety it requires the proper voltage for rated performance,");
  L.push("  and offers 3% as an acceptable figure for such equipment.");
  L.push("  Derating factors are Cut & Crimp's, following ABYC E-11 practice.");
  L.push("");
  L.push("This is a design aid and not a design. The installer remains responsible");
  L.push("for the installation.  Cut & Crimp, 023 8045 5129");
  return L.join("\n");
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
class Calculator {
  constructor(root, payload) {
    this.root = root;
    this.payload = payload;
    this.state = readState(payload);
    this.app = root.querySelector("[data-cc-app]");
    this.fallback = root.querySelector("[data-cc-fallback]");
    this.configureUrl = root.dataset.configureUrl || "/pages/configure";
    this.render();
  }

  render() {
    const s = this.state;
    this.app.innerHTML = `
      <div class="cc-layout">
        ${buildForm(this.payload, s)}
        <div data-cc-result aria-live="polite" aria-atomic="false"></div>
        <div class="cc-actions cc-noprint" data-cc-actions></div>
        ${standardsNote()}
      </div>`;
    this.app.hidden = false;
    if (this.fallback) this.fallback.hidden = true;
    this.bind();
    this.compute();
  }

  bind() {
    const on = (sel, ev, fn) => this.app.querySelectorAll(sel).forEach((n) => n.addEventListener(ev, fn));

    on('input[name="cc-v"]', "change", (e) => { this.state.v = Number(e.target.value); this.compute(); });
    on('input[name="cc-chem"]', "change", (e) => { this.state.chem = e.target.value; this.compute(); });
    on('input[name="cc-crit"]', "change", (e) => { this.state.crit = e.target.value; this.compute(); });
    on('input[name="cc-mode"]', "change", (e) => {
      this.state.mode = e.target.value;
      const label = this.app.querySelector("#cc-load-label");
      const unit = this.app.querySelector("[data-cc-load-unit]");
      if (label) label.textContent = this.state.mode === "watts" ? "Continuous watts" : "Continuous amps";
      if (unit) unit.textContent = this.state.mode === "watts" ? "W" : "A";
      this.compute();
    });

    const num = (id, key, min) => {
      const n = this.app.querySelector(id);
      if (!n) return;
      n.addEventListener("input", () => {
        const v = Number(n.value);
        if (Number.isFinite(v) && v >= min) { this.state[key] = v; this.compute(); }
      });
    };
    num("#cc-load", "load", 0.0001);
    num("#cc-len", "len", 0.0001);
    num("#cc-n", "n", 2);

    on("#cc-env", "change", (e) => { this.state.env = e.target.value; this.compute(); });
    on("#cc-fam", "change", (e) => { this.state.fam = e.target.value; this.compute(); });
    on("#cc-inst", "change", (e) => {
      this.state.inst = e.target.value;
      const wrap = this.app.querySelector("[data-cc-bundle-wrap]");
      if (wrap) wrap.hidden = this.state.inst !== "bundled";
      this.compute();
    });
  }

  request() {
    const s = this.state;
    return {
      systemVoltage: s.v,
      chemistry: s.chem,
      family: s.fam,
      criterion: s.crit,
      environment: s.env,
      install: s.inst === "bundled" ? { bundled: s.n } : s.inst,
      oneWayLengthM: s.len,
      load: s.mode === "watts" ? { watts: s.load } : { amps: s.load },
    };
  }

  compute() {
    const s = this.state;
    writeState(s);

    const result = size(this.payload, this.request());
    this.result = result;

    const box = this.app.querySelector("[data-cc-result]");
    box.innerHTML = renderResult(this.payload, s, result);

    const derived = this.app.querySelector("[data-cc-derived]");
    if (derived) {
      if (s.mode === "watts" && result.ok && result.current) {
        derived.textContent = `That is ${fmtA(result.current.amps)}A of DC current, assuming ${Math.round(
          (result.current.efficiency || 0.9) * 100,
        )}% inverter efficiency.`;
      } else {
        derived.textContent = "";
      }
    }

    this.renderActions();
  }

  renderActions() {
    const box = this.app.querySelector("[data-cc-actions]");
    const r = this.result;
    const hasAnswer = r && r.ok && r.recommendation;
    // Only offer the handoff for a size the configurator can actually build.
    // Handing someone to a page that cannot make what they were just told to
    // buy is worse than not offering the button at all.
    const canConfigure = hasAnswer && r.recommendation.configurable !== false;

    const href = canConfigure
      ? `${this.configureUrl}?f=${encodeURIComponent(this.state.fam)}&csa=${r.recommendation.csa}&len=${Math.round(
          this.state.len * 1000,
        )}`
      : null;

    box.innerHTML = `
      ${href ? `<a class="cc-btn cc-btn--primary" href="${esc(href)}">Configure this cable</a>` : ""}
      <button type="button" class="cc-btn" data-cc-copy>Copy result</button>
      <button type="button" class="cc-btn" data-cc-print>Print result</button>
      ${
        hasAnswer && !canConfigure
          ? `<p class="cc-hint" style="flex-basis:100%;margin:0">We do not make this size up as a finished lead.
             Ring us on <a href="tel:02380455129">023 8045 5129</a> and we will sort it, or buy it by the metre and crimp your own.</p>`
          : ""
      }`;

    const copy = box.querySelector("[data-cc-copy]");
    if (copy) {
      copy.addEventListener("click", async () => {
        const text = asText(this.payload, this.state, this.result, new Date().toLocaleDateString("en-GB", {
          year: "numeric", month: "long", day: "numeric",
        }));
        try {
          await navigator.clipboard.writeText(text);
          copy.textContent = "Copied";
          setTimeout(() => { copy.textContent = "Copy result"; }, 2000);
        } catch {
          // Clipboard blocked. Fall back to a selectable block rather than
          // failing silently, because the installer wanted this text.
          const pre = document.createElement("pre");
          pre.className = "cc-panel cc-mono";
          pre.style.whiteSpace = "pre-wrap";
          pre.textContent = text;
          box.after(pre);
          copy.disabled = true;
        }
      });
    }
    const print = box.querySelector("[data-cc-print]");
    if (print) print.addEventListener("click", () => window.print());
  }
}

async function boot() {
  const root = document.querySelector("[data-cc-calculator]");
  if (!root) return;
  const errorBox = root.querySelector("[data-cc-error]");

  try {
    const payload = await loadPayload(root.dataset.api);
    new Calculator(root, payload);
  } catch (err) {
    // The Liquid fallback stays exactly where it is. A broken tool is worse
    // than no tool, and the page still carries the standards note, a worked
    // example and a phone number.
    console.error("[cc-calculator]", err);
    if (errorBox) errorBox.hidden = false;
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
